/**
 * Wait time calculation utilities for analytics charts
 */

/**
 * Calculate percentage deviation from minimum
 * @param {number[]} values - Array of wait time values
 * @returns {Object} { relativeValues, minimum, percentages, originalValues }
 */
export function calculateRelativeDeviation(values) {
  if (!values || values.length === 0) {
    return { relativeValues: [], minimum: 0, percentages: [], originalValues: [] };
  }

  // Filter out null/undefined values for minimum calculation
  const validValues = values.filter((v) => v !== null && v !== undefined && !isNaN(v));

  if (validValues.length === 0) {
    return { relativeValues: [], minimum: 0, percentages: [], originalValues: values };
  }

  const minimum = Math.min(...validValues);

  // Convert to relative values (minimum becomes 0)
  const relativeValues = values.map((v) => {
    if (v === null || v === undefined || isNaN(v)) return null;
    return v - minimum;
  });

  // Calculate percentage deviation from minimum
  // Using formula: ((value - min) / min) * 100
  // Handle case where minimum is 0
  const percentages = values.map((v) => {
    if (v === null || v === undefined || isNaN(v)) return null;
    if (minimum === 0) {
      return v === 0 ? 0 : 100;
    }
    return ((v - minimum) / minimum) * 100;
  });

  return {
    relativeValues,
    minimum,
    percentages,
    originalValues: values,
  };
}

/**
 * Calculate percentage deviation from maximum (inverse)
 * Higher values become baseline (0%), lower values show savings percentage
 * @param {number[]} values - Array of wait time values
 * @returns {Object} { percentages, maximum, originalValues }
 */
export function calculateInverseDeviation(values) {
  if (!values || values.length === 0) {
    return { percentages: [], maximum: 0, originalValues: [] };
  }

  // Filter out null/undefined values for maximum calculation
  const validValues = values.filter((v) => v !== null && v !== undefined && !isNaN(v));

  if (validValues.length === 0) {
    return { percentages: [], maximum: 0, originalValues: values };
  }

  const maximum = Math.max(...validValues);

  // Calculate percentage deviation from maximum
  // Using formula: ((max - value) / max) * 100
  // Baseline (max) becomes 0%, lower values show positive percentage (savings)
  const percentages = values.map((v) => {
    if (v === null || v === undefined || isNaN(v)) return null;
    if (maximum === 0) {
      return 0;
    }
    return ((maximum - v) / maximum) * 100;
  });

  return {
    percentages,
    maximum,
    originalValues: values,
  };
}

/**
 * Get color based on deviation percentage
 * @param {number} percentage - Deviation percentage
 * @returns {string} Hex color code
 */
export function getDeviationColor(percentage) {
  if (percentage === null || percentage === undefined) return '#999';
  if (percentage === 0) return '#91cc75'; // Green for baseline
  if (percentage < 25) return '#73c0de'; // Light blue for small deviation
  if (percentage < 50) return '#fac858'; // Yellow for medium deviation
  return '#ee6666'; // Red for large deviation
}

/**
 * Get color based on inverse deviation percentage (savings)
 * Higher savings = better (green), baseline = red
 * @param {number} percentage - Savings percentage from maximum
 * @returns {string} Hex color code
 */
export function getInverseDeviationColor(percentage) {
  if (percentage === null || percentage === undefined) return '#999';
  if (percentage === 0) return '#ee6666'; // Red for baseline (highest/worst)
  if (percentage < 15) return '#fc8452'; // Orange for small savings
  if (percentage < 30) return '#fac858'; // Yellow for medium savings
  return '#91cc75'; // Green for large savings (best)
}
